import React from "react";

import NarratorIntonation from "../intonation/NarratorIntonation";
import IntonationFeedbackDisplay from "../intonation/IntonationFeedbackDisplay";
import IntonationHelpModal from "../intonation/IntonationHelpModal";

const IntonationComparisonPanel = ({
  showIntonationVisualization,
  showIntonationHelp,
  setShowIntonationHelp,
  segment,
  assessment,
  subject,
  file,
  handleLaneWordClick,
  broadcastTime,
  audioref,
  visualizationView,
  rate,
  setRate,
  auth,
  resolvedAssessmentAudioUrl,
  resolvedNarratorAssessmentAudioUrl,
}) => {
  if (!showIntonationVisualization) return null;

  return (
    <section className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/40 shadow-lg shadow-blue-500/5 overflow-hidden">
      <div className="px-4 sm:px-5 py-3 border-b border-white/30 bg-gradient-to-r from-white/50 to-white/10">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-white/80 border border-white/60 shadow-sm flex items-center justify-center shrink-0">
            <span className="text-lg">📈</span>
          </div>

          <div className="flex flex-col min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
              

              <span className="hidden sm:inline text-[11px] font-medium text-gray-500 bg-white/70 border border-white/60 px-2 py-0.5 rounded-full whitespace-nowrap">
                Press <span className="font-semibold">V</span> to change view
              </span>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setShowIntonationHelp(true)}
                className="inline-flex items-center gap-2 rounded-xl px-3 py-1.5 bg-blue-50 text-blue-900 border border-blue-200 shadow-sm hover:bg-blue-100 transition text-xs font-semibold"
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-lg bg-white border border-blue-200">
                  ⓘ
                </span>
                How to read the pitch curve
                <span className="text-[10px] font-bold text-blue-700 bg-white/70 border border-blue-200 px-1.5 py-0.5 rounded">
                  30 sec
                </span>
              </button>

              <span className="text-xs text-gray-600">
                <span className="font-semibold text-gray-800">Gaps</span> usually mean
                unvoiced sounds or brief pauses.
              </span>
            </div>
          </div>

          <span className="sm:hidden ml-auto text-[11px] font-medium text-gray-500 bg-white/70 border border-white/60 px-2 py-1 rounded-full">
            V = view
          </span>
        </div>
      </div>

      <div className="divide-y divide-white/25">
        {segment?.words?.length > 0 && (
          <div className="p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center text-white text-xs shadow-sm">
                🎭
              </div>

              <h4 className="font-semibold text-gray-800">Narrator</h4>

              <span className="text-[11px] text-blue-700 bg-blue-50 border border-blue-200/60 px-2 py-0.5 rounded-full">
                Reference
              </span>
            </div>

            <div className="min-h-[220px] sm:min-h-[240px] rounded-xl bg-white/60 border border-white/50 overflow-hidden">
              <div className="p-2 sm:p-3">
                <NarratorIntonation
                  subject={subject}
                  file={file}
                  segment={segment}
                  onWordClick={handleLaneWordClick}
                  broadcastTime={broadcastTime}
                  audioRef={audioref}
                  view={visualizationView}
                  speed={rate}
                />
              </div>

              <div className="px-3 pb-3 pt-2 border-t border-white/40">
                <div className="rounded-xl bg-white/60 border border-white/50 backdrop-blur-sm px-4 py-3 shadow-sm w-full">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="text-sm text-gray-700">
                      <span className="font-medium text-gray-900">Tip:</span>{" "}
                      Click a <span className="font-semibold">word</span> to replay it.
                      If it feels fast, try <span className="font-semibold">0.25×</span>.
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="text-xs font-medium text-gray-500">
                        Speed
                      </span>

                      <div className="inline-flex rounded-xl bg-white border border-gray-200 shadow-sm p-1">
                        {[0.25, 0.5, 1].map((speed) => {
                          const active = rate === speed;

                          return (
                            <button
                              key={speed}
                              onClick={() => {
                                setRate(speed);
                                if (audioref.current) {
                                  audioref.current.playbackRate = speed;
                                }
                              }}
                              className={[
                                "px-3 py-1.5 rounded-lg text-sm font-semibold transition-all",
                                active
                                  ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow"
                                  : "text-gray-600 hover:bg-gray-50",
                              ].join(" ")}
                            >
                              {speed}x
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {assessment && (
          <div className="p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center text-white text-xs shadow-sm">
                🎙️
              </div>

              <h4 className="font-semibold text-gray-800">Your voice</h4>

              <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200/60 px-2 py-0.5 rounded-full">
                Your recording
              </span>

              <div className="ml-auto hidden md:inline text-[11px] text-gray-500">
                Compare your rise/fall pattern to the narrator’s
              </div>
            </div>

            <div className="min-h-[220px] sm:min-h-[240px] rounded-xl bg-white/60 border border-white/50 overflow-hidden">
              <div className="p-2 sm:p-3">
                <IntonationFeedbackDisplay
                  assessment={assessment}
                  segment={segment}
                  view={visualizationView}
                  isAdmin={auth?.role === "ADMIN"}
                  audioUrl={resolvedAssessmentAudioUrl}
                  narratorAudioUrl={resolvedNarratorAssessmentAudioUrl}
                />
              </div>
            </div>
          </div>
        )}

        {!assessment && (
          <div className="p-4 sm:p-5">
            <div className="rounded-xl border border-dashed border-gray-300/70 bg-white/50 p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/70 border border-white/50 flex items-center justify-center">
                🎧
              </div>

              <div>
                <div className="font-semibold text-gray-800">
                  Record to see your curve here
                </div>

                <p className="text-sm text-gray-600">
                  After recording, you'll be able to replay your audio and compare
                  it with the narrator above.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <IntonationHelpModal
        open={showIntonationHelp}
        onClose={() => setShowIntonationHelp(false)}
      />
    </section>
  );
};

export default IntonationComparisonPanel;